import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { getSeoDebate, startSeoDebate, startDemoDebate, getMe } from "@/lib/api";

const AI_AVATAR =
  "https://static.prod-images.emergentagent.com/jobs/b9a6f8ac-ae9e-4cbf-9b6d-e590a64b44ee/images/6c99ee1aa365db3f6f9a5be21aaf930265acd3722b4b50b28b7389055d758d68.png";

const PENDING_SEO_SLUG_KEY = "pending_seo_slug";

export default function SeoPlayPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [debate, setDebate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [user, setUser] = useState(null);
  const autoStartedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const [seo, me] = await Promise.allSettled([getSeoDebate(slug), getMe()]);
        if (seo.status === "fulfilled") {
          setDebate(seo.value);
        } else {
          toast.error("Debate no encontrado.");
          navigate("/", { replace: true });
          return;
        }
        if (me.status === "fulfilled") setUser(me.value);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  const beginDebate = async (authedUser) => {
    setStarting(true);
    try {
      const started = await startSeoDebate(slug);
      navigate("/game", {
        replace: true,
        state: { debate: started, user: authedUser, seoSlug: slug },
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "DAILY_LIMIT_REACHED") {
        toast.error("Llegaste al límite diario. Volvé mañana o hacete PRO.");
        navigate("/game", { replace: true });
      } else {
        toast.error("No se pudo iniciar el debate.");
      }
      setStarting(false);
    }
  };

  const handlePlayGuest = async () => {
    setStarting(true);
    try {
      const started = await startDemoDebate(slug);
      navigate("/demo", {
        replace: true,
        state: { debate: started, seoSlug: slug },
      });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("Este debate ya no está disponible.");
      } else {
        toast.error("No se pudo iniciar la partida. Intentá de nuevo.");
      }
      setStarting(false);
    }
  };

  const handlePlayLoggedIn = async () => {
    await beginDebate(user);
  };

  const handleLoginForSave = () => {
    try {
      localStorage.setItem(PENDING_SEO_SLUG_KEY, slug);
    } catch (_e) {}
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`;
  };

  useEffect(() => {
    if (loading || !user || !debate || autoStartedRef.current || starting) return;
    autoStartedRef.current = true;
    beginDebate(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, debate]);

  if (loading || (user && !starting)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-display text-2xl uppercase">Preparando debate...</div>
      </div>
    );
  }

  if (!debate) return null;

  return (
    <div className="min-h-screen px-4 sm:px-6 py-8 md:py-10 flex flex-col">
      <div className="max-w-2xl mx-auto w-full flex-1 flex flex-col gap-6 justify-center">
        <motion.div
          initial={{ y: -16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center"
        >
          <h1 className="font-display text-4xl sm:text-5xl uppercase leading-[0.9]">
            Conven<span className="text-[#FF4C4C]">ce</span>me
          </h1>
          <p className="font-mono-cm text-xs uppercase tracking-widest text-[#101010]/50 mt-2">
            Debate desde Google
          </p>
        </motion.div>

        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-[#101010] border-4 border-[#101010] rounded-xl cm-shadow overflow-hidden"
        >
          <div className="bg-[#FF4C4C] px-4 py-2">
            <span className="font-mono-cm text-[11px] uppercase tracking-widest text-white font-bold">
              REBECA · {debate.category}
            </span>
          </div>
          <div className="p-5 md:p-7 flex items-start gap-4">
            <img src={AI_AVATAR} alt="REBECA" className="w-16 h-16 md:w-20 md:h-20 object-contain shrink-0" />
            <div>
              <h2 className="font-display text-xl md:text-2xl uppercase text-white leading-tight mb-2">
                {debate.title}
              </h2>
              <p className="text-white/70 text-sm mb-3">{debate.question}</p>
              <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">
                {debate.rebeca_position}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.button
          onClick={user ? handlePlayLoggedIn : handlePlayGuest}
          disabled={starting}
          className="cm-press w-full max-w-sm mx-auto bg-[#FF4C4C] text-white border-4 border-[#101010] rounded-xl px-10 py-4 font-display uppercase text-lg cm-shadow-sm disabled:opacity-60"
        >
          {starting ? "Iniciando..." : "Jugar ahora — 1 ronda gratis"}
        </motion.button>

        {!user && (
          <button
            type="button"
            onClick={handleLoginForSave}
            className="font-mono-cm text-[11px] uppercase tracking-widest text-[#101010]/55 underline underline-offset-4 mx-auto block hover:text-[#101010]"
          >
            Entrar con Google para guardar score y seguir jugando
          </button>
        )}
      </div>
    </div>
  );
}

export { PENDING_SEO_SLUG_KEY };
